import { ApiError, apiRequest } from "@/features/api/apiClient";
import type {
  Blueprint,
  GuidedAnswers,
  GuidedFollowUpAnswers,
  GuidedUnsureFlags,
  TargetBuilder,
} from "@/features/blueprints/blueprintTypes";

type BlueprintResponse = {
  blueprint: Blueprint;
};

type BlueprintsResponse = {
  blueprints: Blueprint[];
};

export function listProjectBlueprints(
  token: string | null,
  projectId: string,
): Promise<BlueprintsResponse> {
  return apiRequest<BlueprintsResponse>(`/api/projects/${projectId}/blueprints`, {
    token,
  });
}

export function createBlueprintDraft(
  token: string | null,
  input: {
    projectId: string;
    title?: string;
    inputText?: string;
    targetBuilder?: TargetBuilder;
  },
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>("/api/blueprints/draft", {
    method: "POST",
    token,
    body: input,
  });
}

/**
 * Creates a draft without a project_id. The backend resolves or creates the
 * user's default "My Blueprints" project automatically (Phase 2A).
 */
export function createQuickBlueprintDraft(
  token: string | null,
  input: {
    title?: string;
    inputText?: string;
    targetBuilder?: TargetBuilder;
  },
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>("/api/blueprints/draft", {
    method: "POST",
    token,
    body: input,
  });
}

export function getBlueprint(
  token: string | null,
  blueprintId: string,
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>(`/api/blueprints/${blueprintId}`, {
    token,
  });
}

export function updateBlueprintDraft(
  token: string | null,
  blueprintId: string,
  updates: {
    title?: string;
    inputText?: string;
    targetBuilder?: TargetBuilder;
  },
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>(`/api/blueprints/${blueprintId}/draft`, {
    method: "PATCH",
    token,
    body: updates,
  });
}

export function updateGuidedBlueprintDraft(
  token: string | null,
  blueprintId: string,
  updates: {
    guidedAnswers: GuidedAnswers;
    smartFollowupAnswers?: GuidedFollowUpAnswers;
    unsureFlags: GuidedUnsureFlags;
    title?: string;
    targetBuilder?: TargetBuilder;
  },
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>(`/api/blueprints/${blueprintId}/guided`, {
    method: "PATCH",
    token,
    body: updates,
  });
}

export function confirmBlueprintDraft(
  token: string | null,
  blueprintId: string,
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>(`/api/blueprints/${blueprintId}/confirm`, {
    method: "POST",
    token,
  });
}

export function generateBlueprint(
  token: string | null,
  blueprintId: string,
  confirmedAssumptions?: string[],
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>(`/api/blueprints/${blueprintId}/generate`, {
    method: "POST",
    token,
    body:
      confirmedAssumptions && confirmedAssumptions.length > 0
        ? { confirmedAssumptions }
        : undefined,
  });
}

export function recordBuildPromptCopied(
  token: string | null,
  blueprintId: string,
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>(
    `/api/blueprints/${blueprintId}/actions/copy-build-prompt`,
    {
      method: "POST",
      token,
    },
  );
}

export function recordBlueprintDownloaded(
  token: string | null,
  blueprintId: string,
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>(
    `/api/blueprints/${blueprintId}/actions/download-markdown`,
    {
      method: "POST",
      token,
    },
  );
}

export async function downloadBlueprintPdf(
  token: string | null,
  blueprintId: string,
): Promise<Blob> {
  const headers = new Headers();

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`/api/blueprints/${blueprintId}/export/pdf`, {
    headers,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new ApiError(
      response.status,
      payload?.message ?? "PDF export is unavailable right now.",
      payload?.code,
    );
  }

  return response.blob();
}

export function rateBlueprint(
  token: string | null,
  blueprintId: string,
  input: { rating: number; feedback?: string },
): Promise<BlueprintResponse> {
  return apiRequest<BlueprintResponse>(`/api/blueprints/${blueprintId}/rating`, {
    method: "POST",
    token,
    body: input,
  });
}

export function deleteBlueprint(
  token: string | null,
  blueprintId: string,
): Promise<void> {
  return apiRequest<void>(`/api/blueprints/${blueprintId}`, {
    method: "DELETE",
    token,
  });
}
